// composeCartModal — the single source of truth for turning a resolved slot
// mapping into the assembled <CartModal>. Both the live configurator
// (SceneDesignerTool's modalRenderOverrides.cart) and the focus-mode section
// preview (FocusHost, focus=section:cart) call this so the two render the
// EXACT same cart — body + totals + footer — from the same slots.
//
// The cart is composed from several slots:
//   modal:cart          — desktop body (product table / eyebrow / title)
//   modal:cart-mobile   — mobile body (narrower table); falls back to desktop
//   modal:cart-totals   — totals row (stat blocks: element count + measurement)
//   modal:cart-footer   — footer action buttons (PDF download / email / close)
//
// Keeping this in one place means the section preview can't drift from what the
// operator actually ships.

import type { ConfiguratorViewerHandle } from "../../../components/ConfiguratorViewer";
import type { PriceBreakdownDto } from "../api/priceClient";
import { Slot } from "../blocks/Slot";
import type { BlockHostContext, SlotMapping } from "../blocks/types";
import { CartModal } from "./CartModal";

export interface ComposeCartModalArgs {
  /** Fully-resolved slot mapping (layout default < saved override < live overlay). */
  mapping: SlotMapping;
  /** Host context — supplies instances, productById, detailCache, getFeature. */
  host: BlockHostContext;
  /** Mobile viewport → prefer the modal:cart-mobile body when authored. */
  isMobile: boolean;
  onClose: () => void;
  baseUrl?: string;
  lang?: string;
  /** Headline (the active series label) shown beneath the eyebrow. */
  headlineTitle?: string;
  /** Live viewer handle for the modal's dimension fetch. Omitted in preview
   *  (the totals slot reads host.getFeature("sceneDimensions") instead). */
  viewerRef?: React.RefObject<ConfiguratorViewerHandle>;
}

/**
 * Compose the assembled Cart modal from its member slots. Pure function of the
 * resolved mapping + host — no side effects, no fetching (the modal warms its
 * own detail cache while open).
 */
export function composeCartModal(args: ComposeCartModalArgs): React.ReactElement {
  const { mapping, host, isMobile, onClose, baseUrl, lang, headlineTitle, viewerRef } = args;

  // Pick the viewport-appropriate cart body. On mobile prefer modal:cart-mobile
  // (a narrower table) and fall back to the desktop body when it wasn't authored.
  const cartBodySlotId =
    isMobile && (mapping["modal:cart-mobile"]?.length ?? 0) > 0 ? "modal:cart-mobile" : "modal:cart";
  const cartBlocks = mapping[cartBodySlotId] ?? [];

  // Legacy cart-items props (fallback fixed-table chrome) always come off the
  // desktop modal:cart slot.
  const cartItemsBlock = (mapping["modal:cart"] ?? []).find(b => b.block === "cart-items");
  const ci = (cartItemsBlock?.props ?? {}) as Record<string, unknown>;

  // Composable body: if the operator authored any non-metadata block in the
  // chosen body slot, render the slot as the cart body. A metadata-only
  // cart-items block keeps the legacy fixed table (back-compat).
  const hasComposableBody = cartBlocks.some(b => b.block !== "cart-items");

  // Live price breakdown from the host's price source — null/undefined when
  // the host has no pricing, which keeps the legacy table exactly as it was.
  const breakdownFeature = host.getFeature("priceBreakdown");
  const breakdown =
    breakdownFeature && typeof breakdownFeature === "object"
      ? (breakdownFeature as PriceBreakdownDto)
      : null;

  return (
    <CartModal
      open
      onClose={onClose}
      instances={host.instances}
      productById={host.productById}
      baseUrl={baseUrl}
      lang={lang}
      detailCache={host.detailCache}
      onCacheDetail={host.onCacheDetail}
      description1Key={typeof ci.description1Key === "string" ? ci.description1Key : undefined}
      description2Key={typeof ci.description2Key === "string" ? ci.description2Key : undefined}
      projectLabel={typeof ci.projectLabel === "string" ? ci.projectLabel : undefined}
      articleNumberLabel={typeof ci.articleNumberLabel === "string" ? ci.articleNumberLabel : undefined}
      priceLabel={typeof ci.priceLabel === "string" ? ci.priceLabel : undefined}
      breakdown={breakdown}
      headlineTitle={headlineTitle}
      viewerRef={viewerRef}
      body={hasComposableBody ? <Slot slotId={cartBodySlotId} mapping={cartBlocks} host={host} inline /> : undefined}
      totals={
        (mapping["modal:cart-totals"]?.length ?? 0) > 0 ? (
          <Slot slotId="modal:cart-totals" mapping={mapping["modal:cart-totals"]} host={host} inline />
        ) : undefined
      }
      actions={<Slot slotId="modal:cart-footer" mapping={mapping["modal:cart-footer"]} host={host} inline />}
    />
  );
}
